import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Utensils, Apple, Beef, Flame, Plus, X,
  Clock, ChefHat, ChevronRight, Search, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { nutritionApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const NutritionPage = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mealForm, setMealForm] = useState({
    date: new Date().toISOString().split('T')[0],
    meal_type: 'lunch',
    food_name: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    quantity_grams: 100
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashData, recipesData, foodsData] = await Promise.all([
        nutritionApi.getDashboard(),
        nutritionApi.getRecipes(),
        nutritionApi.getFoods()
      ]);
      setDashboard(dashData);
      setRecipes(recipesData);
      setFoods(foodsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogMeal = async () => {
    if (!mealForm.food_name || mealForm.calories === 0) {
      toast({
        title: 'Error',
        description: language === 'en' ? 'Please fill in all fields' : 'Por favor completa todos los campos',
        variant: 'destructive'
      });
      return;
    }

    try {
      await nutritionApi.logMeal(mealForm);
      toast({
        title: language === 'en' ? 'Meal Logged!' : '¡Comida Registrada!',
      });
      setShowAddMeal(false);
      setMealForm({
        ...mealForm,
        food_name: '',
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        quantity_grams: 100
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const selectFood = (food) => {
    const multiplier = mealForm.quantity_grams / 100;
    setMealForm({
      ...mealForm,
      food_name: language === 'en' ? food.name_en : food.name_es,
      calories: Math.round(food.calories_per_100g * multiplier),
      protein: Math.round(food.protein_per_100g * multiplier * 10) / 10,
      carbs: Math.round(food.carbs_per_100g * multiplier * 10) / 10,
      fat: Math.round(food.fat_per_100g * multiplier * 10) / 10
    });
    setSearchQuery('');
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await nutritionApi.deleteMeal(mealId);
      toast({
        title: language === 'en' ? 'Meal Deleted' : 'Comida Eliminada',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const tabs = [
    { id: 'overview', label: language === 'en' ? 'Overview' : 'Resumen', icon: Utensils },
    { id: 'meals', label: language === 'en' ? 'Meals' : 'Comidas', icon: Apple },
    { id: 'recipes', label: language === 'en' ? 'Recipes' : 'Recetas', icon: ChefHat }
  ];

  const mealTypes = [
    { value: 'breakfast', label: language === 'en' ? 'Breakfast' : 'Desayuno' },
    { value: 'lunch', label: language === 'en' ? 'Lunch' : 'Almuerzo' },
    { value: 'dinner', label: language === 'en' ? 'Dinner' : 'Cena' },
    { value: 'snack', label: language === 'en' ? 'Snack' : 'Snack' }
  ];

  const filteredFoods = searchQuery.length > 1 
    ? foods.filter(f => 
        f.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.name_es.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  const todayTotals = dashboard?.today?.totals || {};
  const goals = dashboard?.today?.goals || { calories: 2000, protein: 100, carbs: 250, fat: 65 };

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-6 h-6" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">
                {language === 'en' ? 'Nutrition' : 'Nutrición'}
              </h1>
              <p className="text-sm text-gray-400">
                {language === 'en' ? 'Track your meals & macros' : 'Registra tus comidas y macros'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Calories Card */}
            <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-3xl p-6 border border-orange-500/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-gray-400 text-sm">
                    {language === 'en' ? "Today's Calories" : 'Calorías de Hoy'}
                  </p>
                  <p className="text-4xl font-bold text-white">
                    {todayTotals.calories || 0}
                    <span className="text-lg text-gray-400"> / {goals.calories}</span>
                  </p>
                </div>
                <div className="w-20 h-20 rounded-full border-4 border-orange-500/30 flex items-center justify-center">
                  <Flame className="w-8 h-8 text-orange-400" />
                </div>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all"
                  style={{ width: `${Math.min((todayTotals.calories || 0) / goals.calories * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Macros */}
            <div className="grid grid-cols-3 gap-4">
              <MacroCard
                label={language === 'en' ? 'Protein' : 'Proteína'}
                value={todayTotals.protein || 0}
                goal={goals.protein}
                unit="g"
                color="blue"
              />
              <MacroCard
                label={language === 'en' ? 'Carbs' : 'Carbos'}
                value={todayTotals.carbs || 0}
                goal={goals.carbs}
                unit="g"
                color="yellow"
              />
              <MacroCard
                label={language === 'en' ? 'Fat' : 'Grasas'}
                value={todayTotals.fat || 0}
                goal={goals.fat}
                unit="g"
                color="pink"
              />
            </div>

            {/* Quick Add */}
            <Button
              onClick={() => setShowAddMeal(true)}
              className="w-full bg-orange-500 hover:bg-orange-600 h-14"
            >
              <Plus className="w-5 h-5 mr-2" />
              {language === 'en' ? 'Log Meal' : 'Registrar Comida'}
            </Button>

            {/* Today's Meals */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? "Today's Meals" : 'Comidas de Hoy'}
              </h2>
              {dashboard?.today?.meals?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.today.meals.map((meal, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-800/50 p-4 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                          <Utensils className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{meal.food_name}</p>
                          <p className="text-gray-500 text-sm capitalize">{meal.meal_type} • {meal.quantity_grams}g</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-orange-400 font-bold">{meal.calories} kcal</p>
                        <button
                          onClick={() => handleDeleteMeal(meal.id)}
                          className="text-gray-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {language === 'en' ? 'No meals logged today' : 'Sin comidas registradas hoy'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Meals Tab */}
        {activeTab === 'meals' && (
          <div className="space-y-6">
            <Button
              onClick={() => setShowAddMeal(true)}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              {language === 'en' ? 'Add Meal' : 'Agregar Comida'}
            </Button>

            {/* Food Database */}
            <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
              <h2 className="text-lg font-bold text-white mb-4">
                {language === 'en' ? 'Food Database' : 'Base de Datos de Alimentos'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {foods.slice(0, 8).map((food) => (
                  <button
                    key={food.id}
                    onClick={() => {
                      selectFood(food);
                      setShowAddMeal(true);
                    }}
                    className="bg-gray-800/50 p-3 rounded-xl text-center hover:bg-gray-800 transition-colors"
                  >
                    <p className="text-white text-sm font-medium">
                      {language === 'en' ? food.name_en : food.name_es}
                    </p>
                    <p className="text-gray-500 text-xs">{food.calories_per_100g} kcal/100g</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recipes Tab */}
        {activeTab === 'recipes' && (
          <div className="space-y-4">
            {recipes.map((recipe) => (
              <Link
                key={recipe.id}
                to={`/nutrition/recipe/${recipe.id}`}
                className="flex gap-4 bg-gray-900/50 rounded-2xl p-4 border border-gray-800 hover:bg-gray-800/50 transition-colors"
              >
                {recipe.image_url ? (
                  <img 
                    src={recipe.image_url} 
                    alt={recipe.title_en}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <ChefHat className="w-10 h-10 text-orange-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-1">
                    {language === 'en' ? recipe.title_en : recipe.title_es}
                  </h3>
                  <p className="text-gray-500 text-sm mb-2 line-clamp-2">
                    {language === 'en' ? recipe.description_en : recipe.description_es}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {recipe.prep_time_minutes + recipe.cook_time_minutes} min
                    </span>
                    <span>{recipe.calories} kcal</span>
                    <span>{recipe.protein}g protein</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 self-center" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Add Meal Modal */}
      {showAddMeal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center">
          <div className="bg-gray-900 w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {language === 'en' ? 'Log Meal' : 'Registrar Comida'}
              </h2>
              <button onClick={() => setShowAddMeal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Search Foods */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'en' ? 'Search foods...' : 'Buscar alimentos...'}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white"
                />
                {filteredFoods.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl max-h-48 overflow-y-auto z-10">
                    {filteredFoods.map((food) => (
                      <button
                        key={food.id}
                        onClick={() => selectFood(food)}
                        className="w-full p-3 text-left hover:bg-gray-700 text-white text-sm"
                      >
                        {language === 'en' ? food.name_en : food.name_es}
                        <span className="text-gray-500 ml-2">{food.calories_per_100g} kcal</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Meal Type */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">
                  {language === 'en' ? 'Meal Type' : 'Tipo de Comida'}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {mealTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setMealForm({ ...mealForm, meal_type: type.value })}
                      className={`p-2 rounded-xl text-xs font-medium transition-all ${
                        mealForm.meal_type === type.value
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Food Name */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">
                  {language === 'en' ? 'Food Name' : 'Nombre del Alimento'}
                </label>
                <input
                  type="text"
                  value={mealForm.food_name}
                  onChange={(e) => setMealForm({ ...mealForm, food_name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="text-gray-400 text-sm mb-1 block">
                  {language === 'en' ? 'Quantity (grams)' : 'Cantidad (gramos)'}
                </label>
                <input
                  type="number"
                  value={mealForm.quantity_grams}
                  onChange={(e) => setMealForm({ ...mealForm, quantity_grams: parseInt(e.target.value) || 0 })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                />
              </div>

              {/* Macros Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    {language === 'en' ? 'Calories' : 'Calorías'}
                  </label>
                  <input
                    type="number"
                    value={mealForm.calories}
                    onChange={(e) => setMealForm({ ...mealForm, calories: parseInt(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    {language === 'en' ? 'Protein (g)' : 'Proteína (g)'}
                  </label>
                  <input
                    type="number"
                    value={mealForm.protein}
                    onChange={(e) => setMealForm({ ...mealForm, protein: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    {language === 'en' ? 'Carbs (g)' : 'Carbos (g)'}
                  </label>
                  <input
                    type="number"
                    value={mealForm.carbs}
                    onChange={(e) => setMealForm({ ...mealForm, carbs: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    {language === 'en' ? 'Fat (g)' : 'Grasa (g)'}
                  </label>
                  <input
                    type="number"
                    value={mealForm.fat}
                    onChange={(e) => setMealForm({ ...mealForm, fat: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                  />
                </div>
              </div>

              <Button
                onClick={handleLogMeal}
                className="w-full bg-orange-500 hover:bg-orange-600 h-12"
              >
                {language === 'en' ? 'Save Meal' : 'Guardar Comida'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MacroCard = ({ label, value, goal, unit, color }) => {
  const colors = {
    blue: 'text-blue-400 bg-blue-500',
    yellow: 'text-yellow-400 bg-yellow-500',
    pink: 'text-pink-400 bg-pink-500'
  };
  
  const percentage = Math.min((value / goal) * 100, 100);

  return (
    <div className="bg-gray-900/50 rounded-2xl p-4 border border-gray-800">
      <p className="text-gray-400 text-xs mb-2">{label}</p>
      <p className={`text-2xl font-bold ${colors[color].split(' ')[0]}`}>
        {Math.round(value)}<span className="text-sm">{unit}</span>
      </p>
      <div className="h-1 bg-gray-800 rounded-full mt-2 overflow-hidden">
        <div 
          className={`h-full ${colors[color].split(' ')[1]} rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-gray-600 text-xs mt-1">/ {goal}{unit}</p>
    </div>
  );
};

export default NutritionPage;
